import { RootState } from '@/store/types'
import {
  Application,
  ApplicationsState,
  ApplicationSearchConditions,
} from '@/store/applications/types'
import axios from 'axios'
import { ActionTree } from 'vuex'
import _ from 'lodash'
import moment from 'moment'
import { Pager } from '@/store/pager.types'

const convertApplicationByApiResponse = (data: any): Application => ({
  id: data.applicationId,
  type: data.type,
  applicant: {
    id: data.applicantId,
    name: data.applicantName,
  },
  applicationDate: moment(data.applicationDate),
  status: data.status,
  student: data.studentId && {
    id: data.studentId,
    studentNumber: data.studentNumber,
    name: data.studentName,
  },
})

const actions: ActionTree<ApplicationsState, RootState> = {
  /**
   * 申請一覧取得
   *
   * @param {ApplicationSearchConditions} conditions 申請検索条件
   * @param {number} page ページ番号
   */
  fetchApplications: (
    { commit },
    { conditions, page = 1 }: { conditions: ApplicationSearchConditions|null, page: number },
  ) => axios.get('/applications', {
    params: {
      ..._.pickBy({
        applicant: conditions?.targetTutor?.id,
        from: conditions?.from?.format('YYYYMMDD'),
        to: conditions?.to?.format('YYYYMMDD'),
        status: conditions?.statuses?.join(','),
        type: conditions?.types?.join(','),
      }),
      page,
    },
  }).then(({ headers, data }) => {
    const applications: Application[] = _.map(data, convertApplicationByApiResponse)
    const pager: Pager = {
      current: page,
      last: _.chain(headers).get('x-paginate-last').toNumber().value(),
      totalLength: _.chain(headers).get('x-paginate-total').toNumber().value(),
    }

    commit('fetchedApplications', { applications, pager })
  }),

  /**
   * 申請を１件取得する
   * @param {{ applicationId: string // 申請ID }}
   */
  getApplicationById: (
    { commit, dispatch },
    { applicationId }: { applicationId: string },
  ) => axios.get('/applications/', {
    params: {
      id: applicationId,
    },
  }).then(async ({ data }) => {
    const a = _.find(data, { applicationId })
    const application: Application = convertApplicationByApiResponse(a)
    commit('gotApplication', application)

    switch (application.type) {
      case 'vacation':
        await dispatch('getVacationApplication', { applicationId })
        break;
      case 'workOvertime':
        await dispatch('getWorkOvertimeApplication', { applicationId })
        break;
      case 'booth':
        await dispatch('getBoothApplication', { applicationId })
        break;
      case 'spir':
        await dispatch('getSpirApplication', { applicationId })
        break;
      case 'inquiry':
        await dispatch('getEvaluationInquiryApplication', { applicationId })
        break;
      default:
        throw Error(`認識していない申請種別です ${application.type}`)
    }
    return application.type
  }),

  /**
   * 申請を1件承認する
   * @param {{
    *  applicationId: string,  // 申請ID
    *  comment: string, // 承認コメント
    * }}
    */
  approveApplication: (
    context,
    {
      applicationId,
      comment,
    }: { applicationId: string, comment: string },
  ) => axios.post(`/applications/${applicationId}/approve`, {
    comment,
  }),

  /**
   * 申請を1件却下する
   * @param {{
    *  applicationId: string,  // 申請ID
    *  comment: string, // 却下コメント
    * }}
    */
  rejectApplication: (
    context,
    {
      applicationId,
      comment,
    }: { applicationId: string, comment: string },
  ) => axios.post(`/applications/${applicationId}/reject`, {
    comment,
  }),

  /**
   * 申請を1件取り下げる
   * @param {{
   *  applicationId: string,  // 申請ID
   *  comment: string, // 取り下げコメント
   * }}
   */
  withdrawApplication: (
    context,
    {
      applicationId,
      comment,
    }: { applicationId: string, comment: string },
  ) => axios.post(`/applications/${applicationId}/withdraw`, {
    comment,
  }),

  /**
   * 申請の承認をキャンセルする
   * @param {{
   *  applicationId: string,  // 申請ID
   *  comment: string, // 承認キャンセルコメント
   * }}
   */
  cancelApproveApplication: (
    context,
    {
      applicationId,
      comment,
    }: { applicationId: string, comment: string },
  ) => axios.post(`/applications/${applicationId}/cancel`, {
    comment,
  }),
}

export default actions
